/**
 * Financial Forecast Page - View and download the generated forecast
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import financialForecastService, { ForecastMetadata } from '../services/financialForecastService';

const FinancialForecastPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [forecast, setForecast] = useState<ForecastMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadForecast = async () => {
      try {
        const data = await financialForecastService.getMetadata(sessionId!);
        setForecast(data);
        setLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load financial forecast');
        setLoading(false);
      }
    };
    loadForecast();
  }, [sessionId]);

  const handleDownload = () => {
    const url = financialForecastService.getDownloadUrl(sessionId!);
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Loading financial forecast...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
        }}>
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
      {/* Success Header */}
      <div style={{
        textAlign: 'center',
        padding: '40px',
        backgroundColor: '#d4edda',
        border: '2px solid #c3e6cb',
        borderRadius: '8px',
        marginBottom: '32px',
      }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '8px', color: '#155724' }}>
          Financial Forecast Ready
        </h1>
        {forecast && (
          <p style={{ color: '#666', margin: 0 }}>
            Generated: {new Date(forecast.generated_at).toLocaleString()}
          </p>
        )}
      </div>

      {/* Sheets List */}
      {forecast && forecast.sheets.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '1.4rem', marginBottom: '16px' }}>Included Worksheets</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {forecast.sheets.map((sheet, idx) => (
              <div
                key={idx}
                style={{
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <span style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                }}>
                  {idx + 1}
                </span>
                <span style={{ fontSize: '1.1rem' }}>{sheet}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <button
          onClick={handleDownload}
          style={{
            padding: '16px 32px',
            fontSize: '1.1rem',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          Download Financial Forecast (.xlsx)
        </button>

        <button
          onClick={() => navigate(`/reports/${sessionId}`)}
          style={{
            padding: '12px 24px',
            fontSize: '1rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          Back to Reports
        </button>

        <button
          onClick={() => navigate('/sessions')}
          style={{
            padding: '12px 24px',
            fontSize: '1rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          Start New Analysis
        </button>
      </div>
    </div>
  );
};

export default FinancialForecastPage;
